'use client'

import {
  MicrophoneIcon,
  StopIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react'
import { Button } from '@/ui/button'
import { cn } from '@/lib/tailwind'

interface VoiceInputButtonProps {
  isRecording: boolean
  isConnecting?: boolean
  onStartRecording: () => void
  onStopRecording: () => void
  disabled?: boolean
  className?: string
}

export function VoiceInputButton({
  isRecording,
  isConnecting,
  onStartRecording,
  onStopRecording,
  disabled = false,
  className,
}: VoiceInputButtonProps) {
  const isBusy = isConnecting ?? false

  const handleClick = () => {
    if (isRecording) {
      onStopRecording()
    } else {
      onStartRecording()
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={disabled || isBusy}
      className={cn(
        'text-muted-foreground hover:text-foreground relative h-10 w-10 sm:h-8 sm:w-8',
        isRecording && 'text-destructive hover:text-destructive/80',
        className,
      )}
      aria-label={
        isBusy
          ? 'Connecting to voice service'
          : isRecording
            ? 'Stop recording'
            : 'Start voice input'
      }
      title={
        isBusy
          ? 'Connecting...'
          : isRecording
            ? 'Stop recording'
            : 'Start voice input'
      }
    >
      {isBusy ? (
        <CircleNotchIcon className="h-5 w-5 animate-spin sm:h-4 sm:w-4" />
      ) : isRecording ? (
        <StopIcon className="h-5 w-5 sm:h-4 sm:w-4" weight="fill" />
      ) : (
        <MicrophoneIcon className="h-5 w-5 sm:h-4 sm:w-4" />
      )}
      {isRecording && (
        <span className="bg-destructive/20 absolute inset-0 animate-ping rounded-md" />
      )}
    </Button>
  )
}
